import { useCallback, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import {
    ProductState,
    useGetCatalogProductsLazyQuery,
    ProductFragmentFragment,
    useGetDetailedProductLazyQuery,
} from 'data/__generated__';
import { TotemAsyncSelect } from 'components/TotemAsyncSelect';
import { Controller, useFormContext } from 'react-hook-form';
import type { FieldValues, RegisterOptions } from 'react-hook-form';
import { Option } from 'components/TotemSelect';
import type { SingleValue } from 'react-select';
import { useApolloClient } from '@apollo/client';
import { PRODUCT_FRAGMENT } from 'data/fragments/product';

interface ProductSelectFormProps {
    name: string;
    label: string;
    placeholder?: string;
    rules?:
        | Omit<
              RegisterOptions<FieldValues, 'zeroWasteProductTemplateId'>,
              'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
          >
        | undefined;
    defaultOptions?: Option<string>[];
}

export function ProductSelectForm({
    name,
    label,
    placeholder = 'choisir un produit',
    defaultOptions,
}: ProductSelectFormProps) {
    const [fetchProducts] = useGetCatalogProductsLazyQuery();
    const { control, watch } = useFormContext();
    const [selectedProduct, setSelectedProduct] = useState<Option<string> | null>(null);
    const client = useApolloClient();
    const productId = watch<string>(name);

    const [getDetailedProduct] = useGetDetailedProductLazyQuery();

    useEffect(() => {
        const fetchProductDetails = async () => {
            if (productId && !selectedProduct) {
                try {
                    const product =
                        client.readFragment<ProductFragmentFragment>({
                            id: `Product:${productId}`,
                            fragment: PRODUCT_FRAGMENT,
                            fragmentName: 'ProductFragment',
                        }) ||
                        (
                            await getDetailedProduct({
                                variables: {
                                    productId,
                                },
                            })
                        )?.data?.detailedProduct;

                    if (product) {
                        setSelectedProduct({
                            label: product.fullname,
                            value: product._id,
                        });
                    }
                } catch (error) {
                    console.error('Erreur lors de la récupération du produit:', error);
                    toast.error('Erreur lors de la récupération du produit');
                }
            }
        };

        fetchProductDetails();
    }, [productId, client, selectedProduct, getDetailedProduct]);

    const onLoadOptions = useCallback(
        async (input: string) => {
            const searchProductsResult = await fetchProducts({
                variables: {
                    offset: 0,
                    filters: {
                        searchValue: input,
                        states: [ProductState.Regular, ProductState.Created],
                    },
                },
                fetchPolicy: 'cache-and-network',
            });

            if (!searchProductsResult?.data?.catalogProducts?.products) {
                toast.error('Erreur lors de la récupération des produits');
                return [];
            }

            const options = searchProductsResult.data.catalogProducts.products.map(({ _id, fullname }) => ({
                label: fullname,
                value: _id,
            }));

            return options;
        },
        [fetchProducts],
    );

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
                <TotemAsyncSelect<string>
                    {...field}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    loadOptions={onLoadOptions}
                    defaultOptions={defaultOptions}
                    isClearable
                    value={selectedProduct}
                    isMulti={false}
                    onChange={(newValue) => {
                        setSelectedProduct(newValue as SingleValue<Option<string>>);
                        onChange((newValue as SingleValue<Option<string>>)?.value || null);
                    }}
                />
            )}
        />
    );
}
